import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import DashboardLayout from "../../components/DashboardLayout";
import OrbitalLoader from "../../components/common/OrbitalLoader";
import ProductionEmptyState from "../../components/common/ProductionEmptyState";
import ScriptDetailModal from "../../components/modals/ScriptDetailModal";
import ScriptCard from "../../components/script/ScriptCard";
import { brandsService } from "../../services/brands";
import { getTotalPages, parsePaginatedResponse } from "../../utils/pagination";

import { parseScriptsRoute } from "../../utils/scriptRoutes";

const PAGE_SIZE = 10;

const Scripts = ({ currentUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentBrand } = useSelector((state) => state.brand);

  const routeState = useMemo(
    () => parseScriptsRoute(location.pathname),
    [location.pathname],
  );
  const activeScriptHash = routeState.scriptHash;
  const showVideos = routeState.showVideos;
  const showCreateOnMount = new URLSearchParams(location.search).get("create") === "1";

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [scripts, setScripts] = useState([]);
  const [scriptsCount, setScriptsCount] = useState(0);
  const [scriptsNextPageUrl, setScriptsNextPageUrl] = useState(null);
  const [scriptsPrevPageUrl, setScriptsPrevPageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const openScriptDetails = useCallback(
    (scriptHash) => {
      if (!scriptHash) return;
      navigate({
        pathname: `/scripts/${encodeURIComponent(scriptHash)}/`,
        search: location.search,
      });
    },
    [location.search, navigate],
  );

  useEffect(() => {
    if (location.pathname === "/scripts" || location.pathname === "/scripts/") {
      navigate("/scripts/", { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 250);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [currentBrand?.hash]);

  useEffect(() => {
    if (!currentBrand?.hash) {
      setScripts([]);
      setScriptsCount(0);
      return;
    }

    setLoading(true);
    setError(null);
    const params = {
      page,
      page_size: PAGE_SIZE,
      brand_hash: currentBrand.hash,
    };
    if (search) params.search = search;

    brandsService.listScripts(
      params,
      (response) => {
        const { results, count, next, previous } =
          parsePaginatedResponse(response);
        setScripts(results);
        setScriptsCount(count);
        setScriptsNextPageUrl(next);
        setScriptsPrevPageUrl(previous);
        setLoading(false);
      },
      () => {
        setError("Could not load scripts.");
        setLoading(false);
      },
    );
  }, [currentBrand?.hash, page, search]);

  const activeScriptFromList = useMemo(() => {
    if (!activeScriptHash) return null;
    return scripts.find((script) => script.hash === activeScriptHash) || null;
  }, [activeScriptHash, scripts]);

  const totalPages = getTotalPages(scriptsCount, PAGE_SIZE);
  const showPagination = scripts.length > 0 && totalPages > 1;
  const showClearFilters = Boolean(search);
  const isTrulyEmpty = !loading && !error && !search && scriptsCount === 0;

  if (!currentBrand?.hash) {
    return (
      <DashboardLayout title="Scripts" currentUser={currentUser}>
        <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-6 text-theme-text opacity-80">
          Select a brand to view scripts.
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Scripts" currentUser={currentUser}>
      {isTrulyEmpty ? (
        <ProductionEmptyState kind="scripts" />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-semibold text-theme-text">
                Scripts{" "}
                <span className="text-sm font-normal opacity-50">
                  (
                  {scripts.length < scriptsCount
                    ? `${scripts.length}/${scriptsCount}`
                    : scriptsCount}
                  )
                </span>
              </h2>
              {showClearFilters ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput("");
                    setSearch("");
                    setPage(1);
                  }}
                  className="text-xs text-theme-text opacity-75 hover:underline"
                >
                  Clear filters
                </button>
              ) : null}
            </div>
            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search scripts, audiences, patterns"
              className="h-8 w-56 px-2.5 rounded border border-theme-secondary bg-theme-secondary text-theme-text text-xs"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center min-h-[50vh]">
              <OrbitalLoader size={128} />
            </div>
          ) : error ? (
            <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-4 text-theme-text opacity-80">
              {error}
            </div>
          ) : scripts.length === 0 ? (
            <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-6 text-theme-text opacity-80">
              No scripts match these filters.
            </div>
          ) : (
            <div className="space-y-3">
              {scripts.map((script) => (
                <ScriptCard
                  key={script.hash}
                  script={script}
                  onOpen={openScriptDetails}
                />
              ))}
            </div>
          )}

          {showPagination ? (
            <div className="flex items-center justify-between pt-1">
              <p className="text-xs text-theme-text opacity-70">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((value) => Math.max(1, value - 1))}
                  disabled={!scriptsPrevPageUrl || loading}
                  className="px-3 py-1.5 rounded border border-theme-secondary text-sm text-theme-text hover:bg-theme-main disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage((value) => value + 1)}
                  disabled={!scriptsNextPageUrl || loading}
                  className="px-3 py-1.5 rounded border border-theme-secondary text-sm text-theme-text hover:bg-theme-main disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {activeScriptHash ? (
        <ScriptDetailModal
          scriptHash={activeScriptHash}
          initialScript={activeScriptFromList}
          brandHash={currentBrand.hash}
          showVideos={showVideos}
          showCreateOnMount={showCreateOnMount}
          onClose={() =>
            navigate({ pathname: "/scripts/", search: location.search })
          }
          onScriptRegenerated={(newScript) =>
            navigate({
              pathname: `/scripts/${encodeURIComponent(newScript.hash)}/`,
              search: location.search,
            })
          }
        />
      ) : null}
    </DashboardLayout>
  );
};

export default Scripts;
